import '../App.css';

import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import PricingSection from '../components/PricingSection';
import { useSeo } from '../hooks/useSeo';
import { buildSeoUrl, seoConfig } from '../utils/seoConfig';
import { captureProductEvent } from '../utils/productAnalytics';

function PricingPage() {
  const [backendAuth, setBackendAuth] = useState({ isAuthenticated: false, user: null, loading: true });

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await fetch('/auth/status', {
          credentials: 'include',
        });
        if (response.ok) {
          const authData = await response.json();
          setBackendAuth({
            isAuthenticated: authData.isAuthenticated,
            user: authData.user,
            loading: false,
          });
        } else {
          setBackendAuth({ isAuthenticated: false, user: null, loading: false });
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        setBackendAuth({ isAuthenticated: false, user: null, loading: false });
      }
    };

    checkAuthStatus();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('payment_success') === 'true') {
      const plan = params.get('plan') || undefined;
      const interval = params.get('interval') || undefined;
      captureProductEvent('checkout-completed', {
        plan,
        interval,
      });
      if (interval !== 'one_time') {
        captureProductEvent('subscription-started', { plan, interval });
      }
    }

    if (params.get('payment_cancelled') === 'true') {
      captureProductEvent('checkout-canceled');
    }
  }, []);

  const isAuthenticated = backendAuth.isAuthenticated;
  const isLoading = backendAuth.loading;
  const currentUser = backendAuth.user;

  const handleLogin = () => {
    window.location.href = '/login';
  };

  const navigate = useNavigate();

  const handleViewChange = view => {
    if (view === 'pricing') {
      navigate('/pricing');
    } else {
      navigate('/');
    }
  };

  const title = 'Menu AI Pricing - Plans for Menu Translation | Menu AI';
  const description =
    '💳 Choose a plan to translate menus, see dish images, and check nutrition/allergens. Start free and upgrade anytime—clear pricing.';
  const canonical = buildSeoUrl('/pricing');
  const imageAlt = 'Menu AI pricing plans overview';

  useSeo({
    title,
    description,
    canonical,
    robots: 'index, follow',
    keywords:
      'menu translation pricing, menu ai pricing, menu scanner plans, nutrition menu tool',
    og: {
      title,
      description,
      url: canonical,
      image: seoConfig.defaultImage,
      imageAlt,
      type: 'website',
      siteName: seoConfig.siteName,
      locale: seoConfig.locale,
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      url: canonical,
      image: seoConfig.defaultImage,
      imageAlt,
      site: seoConfig.twitterHandle,
      creator: seoConfig.twitterHandle,
      domain: seoConfig.twitterDomain,
    },
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description,
      url: canonical,
      isPartOf: {
        '@type': 'WebSite',
        name: seoConfig.siteName,
        url: seoConfig.baseUrl,
      },
    },
  });

  return (
    <div className="App">
      <Header
        isLoading={isLoading}
        isAuthenticated={isAuthenticated}
        currentUser={currentUser}
        onViewChange={handleViewChange}
      />

      <PricingSection isAuthenticated={isAuthenticated} authLoading={isLoading} onLogin={handleLogin} />

      <footer className="footer">
        <p>
          Made with ❤️ by{' '}
          <a href="https://x.com/jordibosch20" target="_blank" rel="noopener noreferrer">
            @jordibosch20
          </a>
        </p>
      </footer>
    </div>
  );
}

export default PricingPage;
